import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { decodeHtml } from "../../utils/decodeHtml";
import useSmartBack from "../../hooks/useSmartBack";

const FunnelChart = () => {
  const { roleId } = useParams();
  const [steps, setSteps] = useState([]);
  const [users, setUsers] = useState([]);
  const [funnelData, setFunnelData] = useState([]);
  const [roleInfo, setRoleInfo] = useState({ roleName: "", companyName: "", createdDate: null, roleTat: null, industryTat: null, roleStatus: "", lastActivityDate: null,});
  const [summary, setSummary] = useState({}); 
  const [stageSummary, setStageSummary] = useState([]); 
  const [weeklyMovement, setWeeklyMovement] = useState([]); 
  const [stagesMoving, setStagesMoving] = useState({ total_stages: 0, active_stages: 0, }); 
  const [recommendedTat, setRecommendedTat] = useState(null); 
  const [shortlistedTat, setShortlistedTat] = useState(null);
  const [hoverData, setHoverData] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const { setGlobalLoading, token } = useOutletContext();

  const navigate = useNavigate();
  const handleBack = useSmartBack(`/workstation/view/${roleId}`);

  useEffect(() => {
    const fetchFunnel = async () => {
      try {
        setGlobalLoading(true);
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/roles/funnel/${roleId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log("Backend response:", res.data);

        const rawData = res.data.funnelData || [];
        setRoleInfo({ roleName: res.data.roleName || "", companyName: res.data.companyName || "", createdDate: res.data.createdDate || null, roleTat: res.data.roleTat || null, industryTat: res.data.industryTat || null, roleStatus: res.data.roleStatus || "", lastActivityDate: res.data.lastActivityDate || null,});
        setSummary(res.data.summary || {});
        setStageSummary(res.data.stageSummary || []);
        setWeeklyMovement(res.data.weeklyMovement || []);
        setStagesMoving( res.data.stagesMoving || { total_stages: 0, active_stages: 0 } );
        setRecommendedTat(res.data.recommended?.tat_days || null);
        setShortlistedTat(res.data.shortlisted?.tat_days || null);

        const stepMapping = {
          profiles_added: "Profiles Added",
          candidates_briefed: "Candidates Briefed",
          drop_by_pmoves: "Drop by Pmoves",
          candidates_evaluated: "Candidates Evaluated",
          recommended: "Recommended",
          interviews_to_be_set_up: "Shortlisted",
          client_interviews_complete: "Client Interviews Completed",
          client_rejected: "Candidate Rejected by Client",
          offer_negotiation: "Offer & Negotiation",
          offer_accepted_closed: "Offer Accepted & Role closed",
        };

        const priorityColors = ["#4FC3F7", "#FFA726", "#2E7D32"];
        const extraColors = [
          "#AB47BC",
          "#FF7043",
          "#26A69A",
          "#EC407A",
          "#7E57C2",
          "#66BB6A",
          "#FFCA28",
          "#29B6F6",
          "#8D6E63",
          "#9CCC65",
          "#5C6BC0",
        ];

        let funnelData = Object.values(stepMapping).map((stepLabel) => {
          const contributions = {};
          rawData.forEach((row) => {
            const userName =
              row.user_fullname || `${row.firstname} ${row.lastname}`;
            const backendKey = Object.keys(stepMapping).find(
              (k) => stepMapping[k] === stepLabel
            );
            contributions[userName] = row[backendKey] || 0;
          });
          return { step: stepLabel, contributions };
        });

        const steps = funnelData.map((f) => f.step);

        const profilesAdded =
          funnelData.find((f) => f.step === "Profiles Added")?.contributions ||
          {};
        const sortedUsers = Object.keys(profilesAdded)
          .map((user) => ({
            name: user,
            profilesAdded: profilesAdded[user] || 0,
          }))
          .sort((a, b) => b.profilesAdded - a.profilesAdded);

        const usersWithColors = sortedUsers.map((user, i) => ({
          ...user,
          color:
            i < 3
              ? priorityColors[i]
              : extraColors[(i - 3) % extraColors.length],
        }));

        setUsers(usersWithColors);
        setSteps(steps);
        setFunnelData(funnelData);
      } catch (err) {
        console.error("Error fetching funnel data:", err);
        setGlobalLoading(false);
      } finally {
        setGlobalLoading(false);
      }
    };

    fetchFunnel();
  }, [roleId]);

  if (!steps.length || !users.length || !funnelData.length) {
    return <div>Loading funnel data...</div>;
  }

  const toggleUser = (userName) => {
    setSelectedUsers((prev) =>
      prev.includes(userName)
        ? prev.filter((u) => u !== userName)
        : [...prev, userName]
    );
  };

  const activeUsers =
    selectedUsers.length > 0
      ? users.filter((u) => selectedUsers.includes(u.name))
      : users;

  const getTotal = (label) => {
    const obj = funnelData.find((f) => f.step === label);
    if (!obj) return 0;
    return Object.entries(obj.contributions)
      .filter(
        ([user]) => selectedUsers.length === 0 || selectedUsers.includes(user)
      )
      .reduce((a, [, b]) => a + b, 0);
  };

  const totalProfiles = getTotal("Profiles Added");

  const analyticsFlow = [
    ["Profiles Added", "Drop by Pmoves"],
    ["Profiles Added", "Candidates Briefed"],
    ["Candidates Briefed", "Candidates Evaluated"],
    ["Candidates Evaluated", "Recommended"],
    ["Recommended", "Shortlisted"],
    ["Shortlisted", "Client Interviews Completed"],
    ["Client Interviews Completed", "Offer & Negotiation"],
    ["Offer & Negotiation", "Offer Accepted & Role closed"],
  ];

  const analytics = analyticsFlow
    .filter(([from, to]) => steps.includes(from) && steps.includes(to))
    .map(([from, to]) => {
      const fromTotal = getTotal(from);
      const toTotal = getTotal(to);
      const percent =
        fromTotal > 0 ? ((toTotal / fromTotal) * 100).toFixed(1) : "0.0";
      return { from, to, fromTotal, toTotal, percent };
    });
const formatDate = (date) => { 
  if (!date) return "-"; 
  return new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit", }); 
};

const getDisplayStatus = () => {
  const status = roleInfo.roleStatus?.toLowerCase();

  // Active but no activity for 7+ days
  if (
    status === "active" &&
    roleInfo.lastActivityDate
  ) {
    const diffDays = Math.floor(
      (new Date() - new Date(roleInfo.lastActivityDate)) /
        (1000 * 60 * 60 * 24)
    );

    if (diffDays > 7) {
      return {
        text: "On the back burner",
        color: "#F59E0B",
        bg: "#FEF3C7",
        icon: "ti ti-clock-pause",
      };
    }
  }

  // Role status based icon
  switch (status) {
    case "on hold":
    case "shelved":
      return {
        text: roleInfo.roleStatus,
        color: "#F59E0B",
        bg: "#FEF3C7",
        icon: "ti ti-clock-pause",
      };

    case "active":
      return {
        text: roleInfo.roleStatus,
        color: "#16A34A",
        bg: "#DCFCE7",
        icon: "ti ti-circle-check",
      };

    case "closed":
      return {
        text: roleInfo.roleStatus,
        color: "#2563EB",
        bg: "#DBEAFE",
        icon: "ti ti-circle-check",
      };

    default:
      return {
        text: roleInfo.roleStatus,
        color: "#6B7280",
        bg: "#F3F4F6",
        icon: "ti ti-info-circle",
      };
  }
};

  const statusInfo = getDisplayStatus();

  return (
  <>
  <div className="content">
    <div className="d-flex justify-content-between align-items-start mb-1">

  <div style={styles.roleHeader}>
    <div style={styles.roleEyebrow}>ROLE ANALYSIS</div>

    <h2 style={{ margin: "6px 0" }}>
      {decodeHtml(roleInfo.roleName)}
    </h2>

    <div style={styles.roleCompany}>
      {decodeHtml(roleInfo.companyName)}
    </div>

    <div style={styles.roleMeta}>
      <span>Opened {formatDate(roleInfo.createdDate)}</span>
      <span>TAT {roleInfo.roleTat ?? "-"} days</span>
      <span>vs</span>
      <span>Industry {roleInfo.industryTat ?? "-"} days</span>
    </div>
  </div>

  <div
  style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 50,
  }}
>
<div className="d-flex align-items-center gap-2">
  <button className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center" onClick={handleBack}>
    <i className="ti ti-arrow-left me-2"></i> Back
  </button>

  <button className="btn btn-primary btn-sm d-inline-flex align-items-center"  onClick={() => navigate(`/role/view/${roleId}`)}>
    <i className="ti ti-briefcase me-2"></i> Back to Role
  </button>
</div>

  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      background: statusInfo.bg,
      color: statusInfo.color,
      padding: "8px 14px",
      borderRadius: 20,
      fontSize: 13,
      fontWeight: 600,
    }}
  >
    <span>{statusInfo.text}</span>
    <i className={statusInfo.icon}></i>
  </div>
</div>

</div>

<div style={styles.headerDivider}></div>
        
    {/* <div className="row mb-3 col-md-12 col-lg-12 col-xl-12 d-flex"> */}
        {/* Summary Cards */} 
        <div style={styles.summaryGrid}> 
          <div style={styles.summaryCard}> 
            <div style={styles.summaryTitle}>Last Stage Movement</div> 
            <div style={styles.summaryValue}> {summary.days_since_stage_movement ?? "-"} days since any stage moved </div> 
            <div style={styles.summarySub}> {formatDate(summary.last_stage_movement)} </div> 
          </div> 
          <div style={styles.summaryCard}> 
            <div style={styles.summaryTitle}>Last Candidate Briefed</div> 
            <div style={styles.summaryValue}> {formatDate(summary.last_candidate_briefed)} </div> 
            <div style={styles.summarySub}> {summary.days_since_briefed != null ? ` ${summary.days_since_briefed} days ago` : ""} · {summary.total_briefed ?? 0}  briefed in total </div> 
          </div> 
          <div style={styles.summaryCard}> 
            <div style={styles.summaryTitle}>Stages Moving</div> 
            <div style={styles.summaryValue}> {stagesMoving.active_stages} of {stagesMoving.total_stages} </div> 
            <div style={styles.summarySub}> touched in last 30 days </div> 
            </div> 
        </div> 

    {/* </div> */}
    <div style={styles.mainSection}>
    <AnimatePresence>
        {hoverData && (
          <motion.div
            style={{
                ...styles.tooltip,
                left: hoverData.x,
                top: hoverData.y,
                transform: "translateY(-50%)",   // center vertically
            }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            <strong>{hoverData.step}</strong> (Total: {hoverData.total})
            <ul>
              {hoverData.users.map((u, i) => (
                <li key={i}>
                  <span
                    style={{
                      display: "inline-block",
                      width: 10,
                      height: 10,
                      background: u.color,
                      display:"inline-block",
                      marginRight:8,
                      borderRadius:2
                    }}
                  ></span>
                  {u.name}: {hoverData.values[i]}
                </li>
              ))}
            </ul>
          </motion.div>
        )}
    </AnimatePresence>  
        <div style={styles.funnelSection}>
            {/* Funnel */}
            <motion.div
              style={styles.container}
              animate={{ opacity: activeUsers.length === 0 ? 0.3 : 1 }}
              transition={{ duration: 0.5 }}
            >
              <AnimatePresence>
                {steps.map((step, i) => {
                  const stepData = funnelData.find((f) => f.step === step);

                  const stageInfo =
                    stageSummary.find(
                      (s) =>
                        String(s.stage || "").trim().toLowerCase() ===
                        String(step || "").trim().toLowerCase()
                    ) || {};

                  const values = activeUsers.map(
                    (u) => stepData?.contributions[u.name] || 0
                  );

                  const total = values.reduce((a, b) => a + b, 0);

                  if (total === 0) return null;

                  let current = 0;

                  const gradient = values
                    .map((val, j) => {
                      const portion = (val / total) * 100;
                      const start = current;
                      current += portion;

                      return `${activeUsers[j].color} ${start}%, ${activeUsers[j].color} ${current}%`;
                    })
                    .join(",");

                  const width = `${100 - i * 7}%`;

                  return (
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      style={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        marginBottom: 10,
                      }}
                    >
                      {/* Funnel */}
                      <motion.div
                        style={{
                          ...styles.funnelStage,
                          width,
                          background: `linear-gradient(90deg, ${gradient})`,
                        }}
                        whileHover={{ scale: 1.02 }}
                       onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setHoverData({
                            step,
                            total,
                            values,
                            users: activeUsers,
                            x: rect.right + 15, 
                            y: rect.top + rect.height / 2,
                          });
                        }}
                        onMouseLeave={() => setHoverData(null)}
                      >
                        <span style={styles.stageLabel}>
                          {step} : {total}
                        </span>
                      </motion.div>

                      {/* Stage Details */}
                      <div
                        style={{
                          width,
                          textAlign: "center",
                          marginTop: 8,
                          fontSize: 14,
                        }}
                      >
                        <div style={{fontSize: 12}}>
                          <strong>
                            last {formatDate(stageInfo.last_moved)}
                          </strong>

                          {stageInfo.days_ago != null && (
                            <span
                              style={{
                                marginLeft: 10,
                                padding: "3px 10px",
                                borderRadius: 20,
                                background:
                                  stageInfo.health === "Healthy"
                                    ? "#E6F4EA"
                                    : stageInfo.health === "Going Quiet"
                                    ? "#E3F2FD"
                                    : "#FDECEC",
                                color:
                                  stageInfo.health === "Healthy"
                                    ? "#2E7D32"
                                    : stageInfo.health === "Going Quiet"
                                    ? "#1565C0"
                                    : "#C62828",
                                fontWeight: 600,
                              }}
                            >
                              {stageInfo.days_ago}d • {stageInfo.health}
                            </span>
                          )}

                          <span style={{ marginLeft: 10, color: "#666" }}>
                            by {stageInfo.owner || "-"}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </motion.div>
        </div>

        <div style={styles.rightSection}>
            {/* Stage Conversion */}
              <div style={styles.card}> 
                <div style={styles.cardTitle}>Stage Conversion</div> 
                {analytics.map((a, i) => ( 
                  <div key={i} style={styles.analyticsItem}> 
                    <div style={styles.stageRow}> <span> {a.from} → {a.to} : {a.toTotal}</span> <strong>{a.percent}%</strong> </div> 
                    <div style={styles.progressBar}> <div style={{ ...styles.progressFill, width: `${a.percent}%`, }} /> </div> 
                  </div> 
                ))} 
              </div>
            {/* Speed Markers */}
              <div style={styles.card}> 
                <div style={styles.cardTitle}>Speed Markers</div> 
                <div style={styles.metricRow}> <span>1st candidate Recommended</span> {recommendedTat ?? "-"} days from open </div> 
                <div style={styles.metricRow}> <span>1st candidate Shortlisted</span> {shortlistedTat ?? "-"} days from open </div> 
                <div style={styles.metricRow}> <span>TAT so far</span> {roleInfo.roleTat ?? "-"} days (Industry {roleInfo.industryTat ?? "-"})</div> 
            {/* Team Members */}
                <div className="mt-4" style={styles.cardTitle}> Team Members (click to filter)</div>
                {users.map((user, i) => {
                    const isSelected =
                        selectedUsers.length === 0 ||
                        selectedUsers.includes(user.name);
                    return (
                        <motion.div
                            key={i}
                            style={{
                                ...styles.legendItem,
                                opacity: isSelected ? 1 : 0.4,
                                cursor: "pointer",
                                marginBottom: 8,
                            }}
                            whileHover={{ scale: 1.03 }}
                            onClick={() => toggleUser(user.name)}
                        >
                            <span
                                style={{
                                    ...styles.legendColor,
                                    background: user.color,
                                }}
                            />
                            <span>
                                {user.name} ({user.profilesAdded})
                            </span>
                        </motion.div>
                    );
                })}
              </div>                 

           {/* Weekly Activity */}
              <div style={styles.card}>
                <div style={styles.cardTitle}>Weekly Movement</div>

                <div style={styles.weeklyChart}>
                  {(() => {
                    const max = Math.max(...weeklyMovement.map((x) => x.total), 1);

                    return weeklyMovement.map((w, index) => (
                      <div key={index} style={styles.weekColumn}>
                        <span style={styles.weekValue}>{w.total}</span>

                        <div style={styles.barWrapper}>
                          <div
                            style={{
                              ...styles.weekBar,
                              height: `${(w.total / max) * 180}px`,
                            }}
                          />
                        </div>

                        <span style={styles.weekLabel}>W{w.week}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
        </div>

    </div>
  </div>  
  </>  
  );
};

const styles = {
  mainSection: {
    display: "grid",
    gridTemplateColumns: "60% 40%",
    gap: "20px",
    alignItems: "start",
  },

  funnelSection: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #E5E7EB",
    padding: 24,
  },

  rightSection: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  page: {
    width: "100%",
    background: "#F7F9FC",
    padding: "25px",
  },

  container:{
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "15px",
  },

  funnelStage:{
    minHeight: 36,
    borderRadius: 10,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0,0,0,.12)",
  },

  stageLabel: {
    color: "#fff",
    fontWeight: 700,
    fontSize: 18,
  },
  
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    fontSize: "12px",
    marginBottom: "4px",
  },
  legendColor: {
    width: "14px",
    height: "14px",
    borderRadius: "3px",
  },
  tooltip: {
    position: "fixed",
    zIndex: 99999,
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    padding: "12px 14px",
    boxShadow: "0 12px 30px rgba(0,0,0,.18)",
    pointerEvents: "none",
    minWidth: 220,
    transition: "all .15s ease",
  },
  analyticsItem: {
    marginBottom: "12px",
    fontSize: "13px",
    padding: "6px",
    borderRadius: "6px",
    cursor: "pointer",
  },
  progressBar: {
    width: "100%",
    height: "6px",
    background: "#eee",
    borderRadius: "4px",
    marginTop: "4px",
  },
  progressFill: {
    height: "6px",
    background: "#4FC3F7",
    borderRadius: "4px",
  },
  tatCard: {
    margin: "10px 0",
    padding: "8px",
    borderRadius: "6px",
  },
  tatSubText: {
    fontSize: "13px",
    color: "gray",
  },
  roleHeader: { 
    marginBottom: 16, 
  }, 
  roleEyebrow: { 
    fontSize: 12, 
    fontWeight: 700, 
    color: "#667085", 
    letterSpacing: 1, 
  }, 
  roleCompany: { 
    fontSize: 14, 
    color: "#475467", 
    marginBottom: 8, 
  }, 
  roleMeta: { 
    display: "flex", 
    gap: 12, 
    flexWrap: "wrap", 
    fontSize: 12, 
    color: "#667085", 
  }, 
  summaryGrid: { 
    display: "grid", 
    gridTemplateColumns: "2fr 1fr 1fr",
    gap: 16, 
    marginBottom: 16, 
  }, 
  summaryCard: { 
    background: "#fff", 
    border: "1px solid #EAECF0", 
    borderRadius: 12, 
    padding: 14, 
  }, 
  summaryTitle: { 
    fontSize: 12, 
    color: "#667085", 
    marginBottom: 6, 
  }, 
  summaryValue: { 
    fontSize: 24, 
    fontWeight: 700, 
    color: "#101828", 
  }, 
  summarySub: { 
    fontSize: 12, 
    color: "#667085", 
    marginTop: 4, 
  }, 
  card: { 
    background: "#fff", 
    border: "1px solid #EAECF0", 
    borderRadius: 12, 
    padding: 16, 
    marginBottom: 16, 
  }, 
  cardTitle: { 
    fontSize: 16, 
    fontWeight: 700, 
    marginBottom: 12, 
  }, 
  stageCard: { 
    border: "1px solid #F2F4F7", 
    borderRadius: 10, 
    padding: 12, 
    marginBottom: 10, 
  }, 
  stageRow: { 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center", 
  }, 
  stageCount: { 
    fontWeight: 700, 
  }, 
  stageMeta: { 
    fontSize: 12, 
    color: "#667085", 
    marginTop: 4, 
  }, 
  stageOwner: { 
    fontSize: 12, 
    color: "#344054", 
  }, 
  healthBadge: { 
    padding: "4px 8px", 
    borderRadius: 999, 
    fontSize: 11, 
    fontWeight: 700, 
  }, 
  metricRow: { 
    display: "flex", 
    justifyContent: "space-between", 
    padding: "10px 0", 
    borderBottom: "1px solid #F2F4F7", 
  },
  emptyText: {
    textAlign: "center",
    color: "#98A2B3",
    fontSize: 13,
    padding: "15px 0",
  },
  stageInfo:{
    display:"flex",
    justifyContent:"center",
    alignItems:"center",
    gap:10,
    marginTop:-28,
    marginBottom:10,
    fontSize:13,
    color:"#475467",
    flexWrap:"wrap"
  },
  badge:{
    padding:"5px 12px",
    borderRadius:20,
    fontWeight:600,
    fontSize:12
  },
  statusBadge: {
    padding: "4px 10px",
    borderRadius: 20,
    fontWeight: 600,
    fontSize: 12,
  },
  headerDivider: {
    width: "100%",
    height: "1px",
    background: "#D0D5DD",
    marginTop: "1px",
    marginBottom: "10px",
  },
  weeklyChart: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 10,
    height: 240,
    paddingTop: 20,
    borderBottom: "1px solid #E5E7EB",
},

weekColumn: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
},

barWrapper: {
    height: 180,
    display: "flex",
    alignItems: "flex-end",
},

weekBar: {
    width: 45,
    background: "#4FC3F7",
    borderRadius: "8px 8px 0 0",
    transition: "all .3s ease",
},

weekValue: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 6,
},

weekLabel: {
    marginTop: 8,
    fontSize: 11,
    color: "#667085",
},
};

export default FunnelChart;
